import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Loader2, TrendingUp, Calendar, PoundSterling, Users, ChevronDown } from 'lucide-react';

const MONTHS = 6;

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '14d', label: 'Last 14 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'this_year', label: 'This year' },
  { key: 'all', label: 'All time' },
];
const PERIOD_LABELS = Object.fromEntries(PERIODS.map(p => [p.key, p.label]));

function periodSince(key) {
  const now = new Date();
  if (key === 'all') return null;
  if (key === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; }
  if (key === 'this_month') return startOfMonth(now);
  if (key === 'this_year') return new Date(now.getFullYear(), 0, 1);
  const days = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 }[key];
  const d = new Date(now); d.setDate(d.getDate() - days); d.setHours(0, 0, 0, 0); return d;
}

function inPeriod(rec, key) {
  const s = periodSince(key);
  if (!s) return true;
  if (!rec.created_date) return false;
  return new Date(rec.created_date) >= s;
}

function buildMonthBuckets() {
  const buckets = [];
  for (let i = MONTHS - 1; i >= 0; i--) {
    const d = subMonths(new Date(), i);
    buckets.push({
      key: format(d, 'yyyy-MM'),
      label: format(d, 'MMM'),
      start: startOfMonth(d),
      end: endOfMonth(d),
      revenue: 0,
      bookings: 0,
    });
  }
  return buckets;
}

const CustomTooltip = ({ active, payload, label, prefix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#111115] border border-white/10 px-3 py-2 text-xs">
        <p className="text-white/40 mb-1">{label}</p>
        <p className="text-white font-bold">{prefix}{payload[0].value?.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

function StatCard({ icon: Icon, label, value, sub, period, onPeriodChange, loading }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#111115] border border-white/5 p-5 relative">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-primary" />
        <p className="text-primary text-[10px] font-bold tracking-widest">{label}</p>
      </div>
      <p className="text-white font-black text-2xl">{loading ? '…' : value}</p>
      <div className="flex items-center justify-between gap-2 mt-1">
        <p className="text-white/30 text-xs">{sub}</p>
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 text-white/40 hover:text-white text-[11px] font-medium transition-colors"
        >
          {PERIOD_LABELS[period]}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-[#0d0d0f] border border-white/10 py-1">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => { onPeriodChange(p.key); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors ${
                  p.key === period ? 'text-primary bg-primary/5' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminCharts() {
  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['admin-orders-chart'],
    queryFn: () => base44.entities.Order.list('-created_date', 500),
  });

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['admin-bookings-chart'],
    queryFn: () => base44.entities.Booking.list('-created_date', 500),
  });

  const loading = ordersLoading || bookingsLoading;

  const [periods, setPeriods] = useState({
    bookings: 'all',
    bookingRev: 'all',
    shopRev: 'all',
    month: 'this_month',
  });
  const setPeriod = (card) => (key) => setPeriods(s => ({ ...s, [card]: key }));

  const chartData = useMemo(() => {
    const buckets = buildMonthBuckets();

    orders.forEach(o => {
      if (!o.created_date || o.status === 'failed') return;
      const key = format(new Date(o.created_date), 'yyyy-MM');
      const bucket = buckets.find(b => b.key === key);
      if (bucket) bucket.revenue += o.amount || 0;
    });

    bookings.forEach(b => {
      if (!b.created_date || b.status === 'cancelled') return;
      const key = format(new Date(b.created_date), 'yyyy-MM');
      const bucket = buckets.find(bk => bk.key === key);
      if (bucket) bucket.bookings += 1;
    });

    return buckets.map(b => ({
      month: b.label,
      revenue: Math.round(b.revenue),
      bookings: b.bookings,
    }));
  }, [orders, bookings]);

  const totalRevenue = chartData.reduce((s, d) => s + d.revenue, 0);
  const totalBookings = chartData.reduce((s, d) => s + d.bookings, 0);

  const periodBookings = bookings.filter(b => b.status !== 'cancelled' && inPeriod(b, periods.bookings));
  const periodBookingRev = bookings
    .filter(b => b.status !== 'cancelled' && inPeriod(b, periods.bookingRev))
    .reduce((s, b) => s + (b.total_price || 0), 0);
  const periodShopRev = orders
    .filter(o => o.status !== 'failed' && inPeriod(o, periods.shopRev))
    .reduce((s, o) => s + (o.amount || 0), 0);
  const periodMonthBookings = bookings.filter(b => b.status !== 'cancelled' && inPeriod(b, periods.month));
  const periodPending = periodBookings.filter(b => b.status === 'pending').length;

  const stats = [
    {
      icon: Calendar, label: 'TOTAL BOOKINGS',
      value: periodBookings.length,
      sub: `${periodPending} pending`,
      period: periods.bookings, onPeriodChange: setPeriod('bookings'),
    },
    {
      icon: PoundSterling, label: 'BOOKING REVENUE',
      value: `£${periodBookingRev.toLocaleString()}`,
      sub: PERIOD_LABELS[periods.bookingRev],
      period: periods.bookingRev, onPeriodChange: setPeriod('bookingRev'),
    },
    {
      icon: TrendingUp, label: 'SHOP REVENUE',
      value: `£${periodShopRev.toLocaleString()}`,
      sub: PERIOD_LABELS[periods.shopRev],
      period: periods.shopRev, onPeriodChange: setPeriod('shopRev'),
    },
    {
      icon: Users, label: 'BOOKINGS',
      value: periodMonthBookings.length,
      sub: PERIOD_LABELS[periods.month],
      period: periods.month, onPeriodChange: setPeriod('month'),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-10">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <StatCard key={s.label} {...s} loading={loading} />
        ))}
      </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Revenue Chart */}
      <div className="bg-[#111115] border border-white/5 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-primary" />
              <p className="text-primary text-[10px] font-bold tracking-widest">MONTHLY REVENUE</p>
            </div>
            <p className="text-white font-black text-2xl">£{totalRevenue.toLocaleString()}</p>
            <p className="text-white/30 text-xs mt-0.5">Last {MONTHS} months</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={chartData} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(0 88% 45%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(0 88% 45%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `£${v}`} />
            <Tooltip content={<CustomTooltip prefix="£" />} />
            <Area type="monotone" dataKey="revenue" stroke="hsl(0 88% 45%)" strokeWidth={2} fill="url(#revenueGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Bookings Chart */}
      <div className="bg-[#111115] border border-white/5 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-primary" />
              <p className="text-primary text-[10px] font-bold tracking-widest">BOOKING TRENDS</p>
            </div>
            <p className="text-white font-black text-2xl">{totalBookings}</p>
            <p className="text-white/30 text-xs mt-0.5">Last {MONTHS} months</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="bookings" fill="hsl(0 88% 45%)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
    </div>
  );
}